import React, { useEffect, useState } from 'react';
import { Button, FeaturedIcon } from '@myhealth-raccoon/components';
import { Navigate, useNavigate } from 'react-router-dom';
import { useBeforeUnload } from 'react-use';

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { useSelector } from 'src/store';
import { PaymentEmbed } from 'src/components/PaymentEmbed';
import { useAsyncData } from '@myhealth-raccoon/services';
import { patientService } from 'src/services/patient';
import { BackButton } from "src/components/BackButton";
import { ContentContainer } from 'src/components/ContentContainer';
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { innerHtml } from 'src/utils/html';

type Props = {
    nextPage: string;
}

export function Payment({ nextPage }: Props) {
    useBookingFlowStep(4);
    const navigate = useNavigate();
    // show dialog before closing/reloading
    useBeforeUnload();

    const { data: appointmentType } = useSelector(state => state.appointment.appointmentType);
    const siteId = useSelector(state => state.appointment.clinicId);
    const [paymentMethod, setPaymentMethod] = useState<number>();
    const [paymentRequired, setPaymentRequired] = useState<boolean>();
    const [preMessageHtml, setPreMessageHtml] = useState<string>();
    const [preMessageConfirmed, setPreMessageConfirmed] = useState<boolean>();
    const [postMessage, setPostMessage] = useState<string>();
    const [paymentConfirmed, setPaymentConfirmed] = useState<boolean>();
    const [error, setError] = useState<string>();

    const { data: basicDetails } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, siteId);

    useEffect(() => {
        if (!appointmentType) {
            return;
        }
        if (appointmentType.prePaymentRequired && Number(appointmentType.prePaymentFees) > 0) {
            setPaymentMethod(2);
            setPaymentRequired(true);
            setPreMessageHtml(appointmentType.prePaymentMessageInHTML);
            setPostMessage(appointmentType.prePaymentConfirmMessage);
        } else if (appointmentType.postPaymentRequired && Number(appointmentType.postBillingFees) > 0) {
            setPaymentMethod(3);
            setPaymentRequired(true);
            setPreMessageHtml(appointmentType.postBillingMessageInHTML);
            setPostMessage(appointmentType.postBillingConfirmMessageInHTML);
        } else {
            setPaymentRequired(false);
        }
    }, [appointmentType]);

    if (paymentRequired === false) {
        return <Navigate replace to={nextPage} />
    }

    return <>
        {error && <>
            <ContentContainer className="text-center flex-1 lg:flex-none">
                <FeaturedIcon className='mx-auto mb-2.5' />
                <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Payment Error</h1>
            </ContentContainer>
            <StickyBottomBar>
                <BackButton
                    onClick={() => navigate(-1)}
                    label="Back" />
                <Button
                    className='flex-1 text-center flex items-center justify-center gap-2'
                    onClick={() => setError(undefined)}
                >
                    Retry
                </Button>
            </StickyBottomBar>
        </>}

        {!error && !preMessageConfirmed && preMessageHtml && <>
            <ContentContainer className="text-center flex-1 lg:flex-none">
                <FeaturedIcon className='mx-auto mb-2.5' />
                <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Please read the following</h1>
                <div className='prose-sm' {...innerHtml(preMessageHtml)} />
            </ContentContainer>
            <StickyBottomBar>
                <BackButton
                    onClick={() => navigate(-1)}
                    label="No" />
                <Button
                    className='flex-1 text-center flex items-center justify-center gap-2'
                    onClick={() => setPreMessageConfirmed(true)}
                >
                    Yes
                </Button>
            </StickyBottomBar>
        </>}

        {!error && preMessageConfirmed && !paymentConfirmed && paymentMethod && basicDetails &&
            <>
                <ContentContainer className='flex-1 lg:flex-none'>
                    <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Payment</h1>
                    <PaymentEmbed
                        onError={setError}
                        paymentMethod={paymentMethod}
                        onConfirm={() => setPaymentConfirmed(true)}
                        patientName={`${basicDetails.firstname} ${basicDetails.surname}`}
                    />
                </ContentContainer>
                <StickyBottomBar>
                    <Button
                        onClick={() => navigate(-1)}
                        className='flex-1 text-center' color='gray'>Back</Button>
                </StickyBottomBar>
            </>}

        {!error && paymentConfirmed && <>
            <ContentContainer className='text-center flex-1 lg:flex-none'>
                <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Payment Successful</h1>
                {postMessage && <div className='prose-sm' {...innerHtml(postMessage)} />}
            </ContentContainer>
            <StickyBottomBar>
                <Button
                    className='flex-1 text-center flex items-center justify-center gap-2'
                    to={nextPage}
                >
                    Continue
                </Button>
            </StickyBottomBar>
        </>}
    </>
}